import * as THREE from "three";

/*
 * Scroll choreography across the 8 pinned homepage chapters (800vh).
 * CH1 arrival -> CH2 macro -> CH3 portfolio (bottle left + silhouettes) ->
 * CH4 origins (semi-silhouette right) -> CH5 market access (3/4 profile) ->
 * CH6 channels (recede) -> CH7 infrastructure (silhouette left) ->
 * CH8 partnership (centre, warm).
 */
const RAW = [
  { p: 0.0,   cam: [0, 0.7, 8.4],      look: [0, 1.5, 0],       bx: 0,     rot: 0,    sil: 0, key: 0.35, rim: 2.7, amb: 0.025, kc: "#ffd9a8", bg: "#0e0e0e" },
  { p: 0.065, cam: [0, 0.85, 7.2],     look: [0, 1.45, 0],      bx: 0,     rot: 0,    sil: 0, key: 0.6,  rim: 2.3, amb: 0.045, kc: "#ffd9a8", bg: "#111111" },
  { p: 0.143, cam: [0.55, 1.05, 2.35], look: [0, 1.02, 0],      bx: 0,     rot: 0.25, sil: 0, key: 1.7,  rim: 0.9, amb: 0.10,  kc: "#ffe2b8", bg: "#141014" },
  { p: 0.205, cam: [0.35, 1.05, 2.65], look: [0, 1.06, 0],      bx: 0,     rot: 0.4,  sil: 0, key: 1.6,  rim: 1.0, amb: 0.09,  kc: "#ffe2b8", bg: "#151015" },
  { p: 0.27,  cam: [0.8, 1.5, 6.6],    look: [-0.7, 1.25, 0],   bx: -1.45, rot: 0.9,  sil: 1, key: 1.15, rim: 1.7, amb: 0.07,  kc: "#ffd9a8", bg: "#141016" },
  { p: 0.33,  cam: [0.5, 1.45, 6.3],   look: [-0.6, 1.25, 0],   bx: -1.45, rot: 1.15, sil: 1, key: 1.1,  rim: 1.6, amb: 0.07,  kc: "#ffd9a8", bg: "#141016" },
  { p: 0.40,  cam: [0, 1.2, 7.2],      look: [0.7, 1.35, 0],    bx: 1.35,  rot: 2.6,  sil: 0, key: 0.25, rim: 2.3, amb: 0.035, kc: "#ffd9a8", bg: "#0f090c" },
  { p: 0.46,  cam: [0.2, 1.25, 7.0],   look: [0.7, 1.3, 0],     bx: 1.35,  rot: 3.0,  sil: 0, key: 0.3,  rim: 2.2, amb: 0.04,  kc: "#ffd9a8", bg: "#100a0d" },
  { p: 0.53,  cam: [-0.2, 1.15, 6.2],  look: [0.85, 1.25, 0],   bx: 1.5,   rot: 5.6,  sil: 0, key: 1.25, rim: 1.2, amb: 0.08,  kc: "#ffdca8", bg: "#140d10" },
  { p: 0.60,  cam: [0, 1.15, 6.4],     look: [0.8, 1.25, 0],    bx: 1.5,   rot: 5.75, sil: 0, key: 1.2,  rim: 1.2, amb: 0.08,  kc: "#ffdca8", bg: "#140d10" },
  { p: 0.714, cam: [0, 1.7, 10.8],     look: [0, 1.5, 0],       bx: 0,     rot: 6.0,  sil: 0, key: 0.5,  rim: 1.0, amb: 0.05,  kc: "#ffd9a8", bg: "#0f0f10" },
  { p: 0.857, cam: [0.5, 1.15, 7.6],   look: [-1.25, 1.3, 0],   bx: -1.85, rot: 6.28, sil: 0, key: 0.5,  rim: 1.9, amb: 0.06,  kc: "#ffdcb0", bg: "#100e0c" },
  { p: 0.93,  cam: [0.4, 1.3, 6.2],    look: [-0.2, 1.32, 0],   bx: -0.4,  rot: 6.9,  sil: 0, key: 1.7,  rim: 1.1, amb: 0.15,  kc: "#ffc890", bg: "#1c0d13" },
  { p: 1.0,   cam: [0, 1.25, 5.9],     look: [0, 1.38, 0],      bx: 0,     rot: 8.1,  sil: 0, key: 2.3,  rim: 0.8, amb: 0.24,  kc: "#ffbe82", bg: "#220c13" },
];

const FRAMES = RAW.map((k) => ({
  p: k.p,
  cam: new THREE.Vector3(...k.cam),
  look: new THREE.Vector3(...k.look),
  bx: k.bx,
  rot: k.rot,
  sil: k.sil,
  key: k.key,
  rim: k.rim,
  amb: k.amb,
  kc: new THREE.Color(k.kc),
  bg: new THREE.Color(k.bg),
}));

export function makeFrameScratch() {
  return {
    cam: new THREE.Vector3(...RAW[0].cam),
    look: new THREE.Vector3(...RAW[0].look),
    bx: 0,
    rot: 0,
    sil: 0,
    key: RAW[0].key,
    rim: RAW[0].rim,
    amb: RAW[0].amb,
    kc: new THREE.Color(RAW[0].kc),
    bg: new THREE.Color(RAW[0].bg),
  };
}

export function getFrame(p, isMobile, out) {
  let i = 0;
  while (i < FRAMES.length - 2 && p > FRAMES[i + 1].p) i++;
  const a = FRAMES[i];
  const b = FRAMES[i + 1];
  let t = (p - a.p) / (b.p - a.p);
  t = Math.min(1, Math.max(0, t));
  t = t * t * (3 - 2 * t); // smoothstep

  out.cam.lerpVectors(a.cam, b.cam, t);
  out.look.lerpVectors(a.look, b.look, t);
  out.bx = a.bx + (b.bx - a.bx) * t;
  out.rot = a.rot + (b.rot - a.rot) * t;
  out.sil = a.sil + (b.sil - a.sil) * t;
  out.key = a.key + (b.key - a.key) * t;
  out.rim = a.rim + (b.rim - a.rim) * t;
  out.amb = a.amb + (b.amb - a.amb) * t;
  out.kc.lerpColors(a.kc, b.kc, t);
  out.bg.lerpColors(a.bg, b.bg, t);

  if (isMobile) {
    out.cam.z = out.cam.z * 1.3 + 0.4;
    out.cam.x *= 0.7;
    out.cam.y += 0.12;
    out.look.y -= 0.4;
    out.bx *= 0.55;
    out.look.x *= 0.55;
  }
  return out;
}

/* Well-lit hero framing used by reduced-motion / static presentations */
export function getStaticFrame(isMobile, out) {
  out.cam.set(0.4, 1.15, isMobile ? 7.0 : 5.7);
  out.look.set(0, 1.35, 0);
  out.bx = 0;
  out.rot = 0.35;
  out.sil = 0;
  out.key = 1.9;
  out.rim = 1.35;
  out.amb = 0.16;
  out.kc.set("#ffcf9c");
  out.bg.set("#131013");
  return out;
}

/* Smooth in/out window for DOM overlay opacity: 0 before a, 1 between b..c, 0 after d */
export function windowOpacity(p, a, b, c, d) {
  const rise = Math.min(1, Math.max(0, (p - a) / (b - a)));
  const fall = 1 - Math.min(1, Math.max(0, (p - c) / (d - c)));
  const v = Math.min(rise, fall);
  return v * v * (3 - 2 * v);
}
